import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

import type { RoutePathDefinition } from '@/types/routes';

import { findActiveTopLevelSubmenuPath } from './submenuRouteMatch';

type SidebarSubmenuAccordionContextValue = {
  /** Which top-level `isSubMenu` `path` is expanded (only one). */
  openSubmenuPath: string | null;
  /** Toggle: same path closes; another path opens and implicitly closes the previous. */
  toggleSubmenu: (path: string) => void;
};

const SidebarSubmenuAccordionContext = createContext<SidebarSubmenuAccordionContextValue | null>(
  null
);

export const SidebarSubmenuAccordionProvider = ({
  children,
  routes,
}: {
  children: ReactNode;
  routes: RoutePathDefinition[];
}) => {
  const { pathname } = useLocation();

  const [openSubmenuPath, setOpenSubmenuPath] = useState<string | null>(() =>
    findActiveTopLevelSubmenuPath(routes, pathname)
  );

  const initializedRef = useRef(true);

  useEffect(() => {
    if (initializedRef.current) {
      initializedRef.current = false;
      return;
    }
    setOpenSubmenuPath(findActiveTopLevelSubmenuPath(routes, pathname));
  }, [pathname, routes]);

  const toggleSubmenu = useCallback((path: string) => {
    setOpenSubmenuPath((prev) => (prev === path ? null : path));
  }, []);

  const value = useMemo(
    () => ({ openSubmenuPath, toggleSubmenu }),
    [openSubmenuPath, toggleSubmenu]
  );

  return (
    <SidebarSubmenuAccordionContext.Provider value={value}>
      {children}
    </SidebarSubmenuAccordionContext.Provider>
  );
};

export function useSidebarSubmenuAccordion(): SidebarSubmenuAccordionContextValue {
  const ctx = useContext(SidebarSubmenuAccordionContext);
  if (!ctx) {
    throw new Error(
      'useSidebarSubmenuAccordion must be used within SidebarSubmenuAccordionProvider'
    );
  }
  return ctx;
}
